/** external deps */
import React, { useEffect, useState } from "react";
import { Select, Form, Spin, Empty, Tooltip, Checkbox } from "antd";

/** internal deps */
import { getInitials } from "helpers/GeneralHelper";
import { IconDropdown } from "assets/icons/icons";
import LOCALIZATION from "../../../../services/LocalizationService";

import "./style.scss";

const { Option } = Select;

function Dropdown({
  name,
  label,
  showSearch,
  allowSearch, // alias for showSearch
  placeholder,
  options,
  onChange,
  defaultValue,
  allowClear,
  disabled,
  required,
  dropdownWidth,
  loading,
  onKeyUp,
  onKeyDown,
  readOnly,
  validator,
  validateTrigger,
  notwrapInForm,
  mode,
  value,
  onClick,
  className,
  onDeselect,
  onClear,
  onPressEnter,
  selectedUser,
  prefix,
  style,
  selectClassName,
  onSearch,
  onselect,
  selectedOption,
  projects,
  selecteddata,
  rules: propRules,
}) {
  const [selectedValue, setSelectedValue] = useState(value !== undefined ? value : defaultValue);
  const sortedOption = options;

  /**  Validations rules */
  let rules = [
    { required: required === true, message: LOCALIZATION.REQUIRED },
  ];
  if (propRules && Array.isArray(propRules) && propRules.length > 0) {
    rules = rules.concat(propRules);
  }
  if (validator) {
    rules.push({ validator });
  }

  const handleChange = (props) => {
    // Handle both single and multiple selection modes
    if (mode === 'multiple') {
      // For multiple mode, props is an array of values
      setSelectedValue(props || []);
    } else {
      // For single mode, props is a single value
      const user = options?.find((item) => item?.value === props);
      setSelectedValue(user);
    }
    if (onChange) {
      onChange(props);
    }
  };
  // Determine the value to use - ensure it's an array for multiple mode
  const getSelectValue = () => {
    if (value !== undefined) {
      // Controlled mode - ensure array for multiple mode
      if (mode === 'multiple') {
        return Array.isArray(value) ? value : [];
      }
      return value;
    }
    // Uncontrolled mode
    if (mode === 'multiple') {
      return Array.isArray(selectedValue) ? selectedValue : [];
    }
    return selectedValue;
  };

  const DropdownList = (
    <Select
      className={`custom-select ${selectClassName}`}
      mode={mode}
      showSearch={showSearch === false || allowSearch === false ? false : true}
      allowClear={allowClear === false ? false : true}
      defaultValue={value === undefined ? defaultValue : undefined}
      value={getSelectValue()}
      placeholder={placeholder || LOCALIZATION.PLEASE_SELECT}
      onChange={handleChange}
      onClear={onClear}
      onKeyUp={onKeyUp}
      onSearch={onSearch}
      onSelect={onselect}
      onKeyDown={onKeyDown}
      disabled={disabled}
      onClick={onClick}
      loading={loading}
      optionFilterProp="label"
      filterOption={(input, option) => {
        // If no search input, show all options
        if (!input || input.trim() === '') return true;
        // Otherwise filter by label
        const label = option?.label ?? '';
        return label.toLowerCase().includes(input.toLowerCase());
      }}
      onPressEnter={onPressEnter}
      onDeselect={onDeselect}
      suffixIcon={<IconDropdown className={""} />}
      showArrow={!disabled}
      readOnly={readOnly}
      style={{ width: dropdownWidth, ...style }}
      maxTagCount="responsive"
      dropdownMatchSelectWidth={false}
      virtual={false}
      listHeight={300}
      maxTagPlaceholder={(omittedValues) => (
        <Tooltip
          placement="bottomRight"
          title={
            <div>
              {omittedValues.map(({ label }, index) => (
                <div key={index}>{label}</div>
              ))}
            </div>
          }
        > 
          <span>+{omittedValues.length}</span>
        </Tooltip>
      )}
      notFoundContent={
        loading ? (
          <div style={{ textAlign: "center" }}>
            <Spin size="small" />
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )
      }
    >
      {sortedOption &&
        sortedOption.map((data, index) => {
          return (
            <Option
              disabled={data?.disabled}
              key={data.value ?? `option-${index}`}
              title={data.name}
              value={data.value}
              label={data.name}
            >
              {data.icon}{data.icon ? ' ' : ''}{data.name}
            </Option>
          );
        })}

      {selectedOption?.length > 0 &&
        selectedOption?.map((item, index) => {
          return (
            <Option 
              key={item.value ?? `selected-${index}`} 
              title={item.name} 
              value={item.value} 
              label={item.name}
            >
              {item.icon}{item.icon ? ' ' : ''}{item.name}
            </Option>
          );
        })}
    </Select>
  );

  useEffect(() => {
    // Update selectedValue when value prop changes (controlled mode)
    if (value !== undefined) {
      setSelectedValue(value);
    } else if (defaultValue !== undefined) {
      // Only update from defaultValue if value is not provided (uncontrolled mode)
      setSelectedValue(defaultValue);
    }
  }, [value, defaultValue]);

  return (
    <div
      className={
        !prefix
          ? "custom-select-prefix-ident"
          : "relative custom-select-component"
      }
    >
      {prefix && (
        <span className="nameCircle">
          {getInitials(selectedValue?.name ?? selectedUser)}
        </span>
      )}
      {notwrapInForm ? (
        <> {DropdownList}</>
      ) : (
        <Form.Item
          name={name}
          className={className}
          label={label}
          validateTrigger={validateTrigger || "onBlur"}
          rules={rules}
        >
          {DropdownList}
        </Form.Item>
      )}
    </div>
  );
}

export default Dropdown;
